import dayjs, { Dayjs } from 'dayjs';
import Swal, { SweetAlertResult } from 'sweetalert2';

import Fetch from 'utils/fetch';
import { isAfterNow } from 'utils/date';
import { settings } from 'settings';
import {
  PusherEvent,
  subscribeToChannel,
  unsubscribeFromChannel,
} from './pusher';
import { Order, OrderListOrder, Region } from '../types/orderTypes';
import { getHubConfig } from '../const/HubConfig';

export interface AutoBundlerProgressReport {
  id?: string;
  progress: number;
  failed: boolean;
  error?: string;
  metadata: { [key: string]: string };
  started?: string;
}

export enum AutoBundlerSchedule {
  SHORT,
  MEDIUM,
  LONG,
}

export async function bundleServiceModal(date: string): Promise<{
  timePerStop?: number;
  timeToShowOrdersFor?: string;
  cancelled: boolean;
}> {
  const resultTimePerStop: SweetAlertResult<string> = await Swal.fire({
    title: 'Enter estimated waiting time per stop',
    input: 'text',
    inputValue: '5',
    showCancelButton: true,
    inputValidator: value => {
      return (!value && 'You need to write something!') || null;
    },
  });
  const { value: timePerStop } = resultTimePerStop;

  if (timePerStop) {
    const resultStartTime: SweetAlertResult<string> = await Swal.fire({
      title: 'When will the driver depart the Hub?',
      input: 'text',
      inputValue: '17:00',
      showCancelButton: true,
      inputValidator: value => {
        return (
          (!value && 'You need to write something!') ||
          (!/^\d{2}:\d{2}$/.test(value) &&
            'the time has to be of the format 04:00') ||
          null
        );
      },
    });
    const { value: startTime } = resultStartTime;

    if (startTime) {
      await Swal.fire(
        `Creating bundle with ${timePerStop} min estimated waiting time per stop and start time is ${startTime}`
      );
    } else {
      return { cancelled: true };
    }

    const timeToShowOrdersFor = dayjs(date)
      .set('hour', parseInt(startTime.split(':')[0], 10))
      .set('minute', parseInt(startTime.split(':')[1], 10))
      .set('second', 0)
      .format();

    const timePerStopValue = parseInt(timePerStop, 10);
    return {
      timePerStop: timePerStopValue,
      timeToShowOrdersFor,
      cancelled: false,
    };
  } else {
    return { cancelled: true };
  }
}

export async function runBundlingAlgorithm(
  orders: (Order | OrderListOrder)[],
  date: Dayjs,
  region: Region,
  timePerStop: number,
  departureTime: string,
  createBundles: boolean,
  context: string,
  onSuccess: () => void,
  onError: (error: any) => void,
  onProgress: (progress: AutoBundlerProgressReport) => void
): Promise<boolean> {
  const startTime = date
    .set('hour', parseInt(departureTime.split(':')[0], 10))
    .set('minute', parseInt(departureTime.split(':')[1], 10))
    .set('second', 0)
    .format();

  await runAlgorithm(
    orders,
    timePerStop,
    startTime,
    `${settings.newApiUrl}/bundle/auto/run-from-frontend`,
    region,
    context,
    onSuccess,
    onError,
    onProgress,
    undefined,
    createBundles
  );
  return true;
}

function autoBundlerAlpha(schedule?: AutoBundlerSchedule): number | undefined {
  if (schedule === AutoBundlerSchedule.MEDIUM) {
    return 0.5;
  } else if (schedule === AutoBundlerSchedule.SHORT) {
    return 0.0;
  }
  return undefined;
}

export async function runAlgorithm(
  orders: (Order | OrderListOrder)[],
  timePerStop: number,
  startTime: string,
  url: string,
  region: Region,
  context: string,
  successHandler: (data: any) => void,
  errorHandler: (data: any) => void,
  progressHandler?: (progress: AutoBundlerProgressReport) => void,
  schedule: AutoBundlerSchedule = AutoBundlerSchedule.LONG,
  createBundles = true
): Promise<void> {
  try {
    const response = await mapAndPerformAlgorithmRequest(
      orders,
      timePerStop,
      startTime,
      url,
      region,
      context,
      autoBundlerAlpha(schedule),
      createBundles
    );
    const sessionChannel = response.sessionTopic;
    subscribeToChannel(sessionChannel, PusherEvent.COMPLETED, data => {
      successHandler(data);
      unsubscribeFromChannel(sessionChannel);
    });
    subscribeToChannel(sessionChannel, PusherEvent.ERROR, data => {
      errorHandler(data);
      unsubscribeFromChannel(sessionChannel);
    });
    if (progressHandler) {
      subscribeToChannel(
        sessionChannel,
        PusherEvent.PROGRESS,
        (progress: AutoBundlerProgressReport) => {
          progressHandler(progress);
        }
      );
    }
  } catch (err) {
    errorHandler(err);
  }
}

async function mapAndPerformAlgorithmRequest(
  orders: (Order | OrderListOrder)[],
  timePerStop: number,
  startTime: string,
  url: string,
  region: Region,
  context: string,
  alpha?: number,
  createBundles = true
) {
  if (!dayjs(startTime, 'HH:mm').isValid()) {
    throw new Error('The start time is not a valid start time');
  }

  const ordersWithDeliveryUntilAfterNow = createBundles
    ? orders.filter(order => isAfterNow(order.deliverUntil))
    : orders;

  const timeToShowOrdersFor = dayjs(new Date())
    .add(0, 'day')
    .set('hour', 17)
    .set('minute', 0)
    .set('second', 0)
    .format();

  const bundlingServiceRequestData = await mapToBundlingServiceRequestData(
    ordersWithDeliveryUntilAfterNow,
    timePerStop,
    startTime ? startTime : timeToShowOrdersFor,
    region,
    context,
    alpha,
    createBundles
  );

  const response = await Fetch.post({
    url: `${url}`,
    body: bundlingServiceRequestData,
  });

  if (response.ok) {
    return await response.json();
  } else {
    throw new Error(
      `The post to the bundling service did not return ok. ${response}`
    );
  }
}

export async function subscribeToBundler(
  region: Region,
  context: string,
  successHandler: (data: any) => void,
  errorHandler: (data: any) => void,
  progressHandler: (progress: AutoBundlerProgressReport) => void
): Promise<void> {
  try {
    const response = await Fetch.get({
      url: `${settings.newApiUrl}/bundle/auto/status?region=${region}&context=${context}`,
    });

    if (response.status !== 200) {
      return; // status = 204 if no bundler in progress
    }
    console.log('resp');
    const resp: {
      session: { sessionTopic: string };
      status: AutoBundlerProgressReport;
    } = await response.json();

    progressHandler(resp.status);

    const sessionChannel = resp.session.sessionTopic;
    subscribeToChannel(sessionChannel, PusherEvent.COMPLETED, data => {
      successHandler(data);
      unsubscribeFromChannel(sessionChannel);
    });
    subscribeToChannel(sessionChannel, PusherEvent.ERROR, data => {
      errorHandler(data);
      unsubscribeFromChannel(sessionChannel);
    });
    subscribeToChannel(
      sessionChannel,
      PusherEvent.PROGRESS,
      (progress: AutoBundlerProgressReport) => {
        progressHandler(progress);
      }
    );
  } catch (err) {
    console.error(err);
  }
}

function getOrderIdAndLatLngFromOrder(order: Order | OrderListOrder) {
  return {
    id: order.id,
    pickupLocation: {
      lat: order.originAddress.location.latitude,
      lng: order.originAddress.location.longitude,
    },
    deliveryLocation: {
      lat: order.destinationAddress?.location.latitude,
      lng: order.destinationAddress?.location.longitude,
    },
    timeWindow: {
      from: order.deliverFrom,
      until: order.deliverUntil,
    },
  };
}

async function mapToBundlingServiceRequestData(
  orders: (Order | OrderListOrder)[],
  timePerStop: number,
  startTime: string,
  region: Region,
  context: string,
  alpha?: number,
  createBundles = true
) {
  const bundleServiceOrders = orders.map(order =>
    getOrderIdAndLatLngFromOrder(order)
  );
  return {
    orders: bundleServiceOrders,
    startLocation: {
      lat: getHubConfig(region)?.hubAddress.location.latitude,
      lng: getHubConfig(region)?.hubAddress.location.longitude,
    },
    startTime: dayjs(startTime, 'HH:mm').format(),
    timePerStop: timePerStop,
    apiKey: settings.googleMapsApiKey,
    metadata: {
      region: region,
      context: context,
      skipCreate: !createBundles,
    },
    alpha: alpha,
  };
}
